// ─────────────────────────────────────────────
// Shared utilities for Safety Inspection Dashboards
// ─────────────────────────────────────────────

/**
* Returns the current user's ID from localStorage USER_DATA.
*/
export function getCurrentUserId() {
   try {
       const raw = localStorage.getItem("USER_DATA");
       if (!raw || raw === "undefined") return null;
       const user = JSON.parse(raw);
       return user?.id ?? user?.user_id ?? null;
   } catch {
       return null;
   }
}

/**
 * Returns parsed USER_DATA object from localStorage or JWT token payload fallback.
 */
export function getUserData() {
  try {
    const raw = localStorage.getItem("USER_DATA");
    if (raw && raw !== "undefined") return JSON.parse(raw);
  } catch { /* ignore */ }

  try {
    const token = localStorage.getItem("token") || localStorage.getItem("ACCESS_TOKEN");
    if (token && typeof token === "string" && token.includes(".")) {
      const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      if (payload) return payload;
    }
  } catch { /* ignore */ }

  return null;
}

/**
 * Returns active project ID from localStorage or JWT token payload.
 */
export function resolveActiveProjectId() {
  const pid =
    localStorage.getItem("ACTIVE_PROJECT_ID") ||
    localStorage.getItem("PROJECT_ID") ||
    localStorage.getItem("project_id") ||
    localStorage.getItem("active_project_id");

  if (pid) return pid;

  try {
    const token = localStorage.getItem("token") || localStorage.getItem("ACCESS_TOKEN");
    if (token && typeof token === "string" && token.includes(".")) {
      const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      if (payload?.active_project_id || payload?.project_id) {
        return payload.active_project_id || payload.project_id;
      }
    }
  } catch { /* ignore */ }

  return null;
}

/**
* Returns the current user's name from localStorage USER_DATA.
*/
export function getCurrentUserName() {
   try {
       const raw = localStorage.getItem("USER_DATA");
       if (!raw || raw === "undefined") return "";
       const user = JSON.parse(raw);
       const firstName = user?.first_name || user?.firstName || "";
       const lastName = user?.last_name || user?.lastName || "";
       const fullName = `${firstName} ${lastName}`.trim();
       if (fullName) return fullName;
       return user?.name || user?.username || user?.full_name || user?.email || "";
   } catch {
       return "";
   }
}

/**
* Converts a File object to a base64 string (without the data-URL prefix).
*/
export function fileToBase64(file) {
   return new Promise((resolve, reject) => {
       const reader = new FileReader();
       reader.onload = () => {
           const result = reader.result || "";
           const commaIndex = result.indexOf(",");
           resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
       };
       reader.onerror = reject;
       reader.readAsDataURL(file);
   });
}

/**
* Resolve which dashboard to show based on the logged-in user's role.
*
* Priority:
*   1) SAFETY_INSPECTION_ROLE or FLOW_ROLE in localStorage
*   2) ROLE in localStorage
*   3) USER_DATA.role / USER_DATA.roles[0]
*   4) Derived from assigned checklists (checker / maker / both)
*
* Returns: "checker" | "maker" | "both" | "initializer" | "supervisor" | null
*/
export function getSafetyInspectionRole(checklists, userId) {
   const raw = (v) => (typeof v === "string" ? v : "").trim().toLowerCase();

   const CHECKER_ROLES = ["checker"];
   const MAKER_ROLES = ["maker"];
   const INITIALIZER_ROLES = ["initializer", "intializer"]; // handles backend typo "INTIALIZER"
   const SUPERVISOR_ROLES = ["supervisor"];

   // Substring-inclusive matching (e.g. "safety_initializer", "SAFETY-INSPECTION-CHECKER")
   const matchesRole = (roleValue, keywords) => {
       const r = raw(roleValue);
       if (!r) return false;
       return keywords.some((kw) => r === kw || r.includes(kw));
   };

   let role = raw(
       localStorage.getItem("SAFETY_INSPECTION_ROLE") ||
       localStorage.getItem("FLOW_ROLE")
   );
   if (!role) role = raw(localStorage.getItem("ROLE"));
   if (!role) {
       try {
           const ud = localStorage.getItem("USER_DATA");
           if (ud && ud !== "undefined") {
               const data = JSON.parse(ud);
               role = raw(data?.role || data?.roles?.[0]);
           }
       } catch { /* ignore */ }
   }

   if (matchesRole(role, CHECKER_ROLES)) return "checker";
   if (matchesRole(role, MAKER_ROLES)) return "maker";
   if (matchesRole(role, INITIALIZER_ROLES)) return "initializer";
   if (matchesRole(role, SUPERVISOR_ROLES)) return "supervisor";

   // Fallback: derive from checklist assignment metadata
   if (checklists && userId != null) {
       const asChecker = checklists.filter(
           (c) =>
               Number(c.current_assignee_id) === Number(userId) &&
               (c.current_assignee_role || "").toUpperCase() === "CHECKER"
       );
       const asMaker = checklists.filter(
           (c) =>
               Number(c.current_assignee_id) === Number(userId) &&
               (c.current_assignee_role || "").toUpperCase() === "MAKER"
       );
       if (asChecker.length > 0 && asMaker.length === 0) return "checker";
       if (asMaker.length > 0 && asChecker.length === 0) return "maker";
       if (asChecker.length > 0 && asMaker.length > 0) return "both";
   }

   return null;
}

/**
 * Resolves all active UserAccess objects for a specific project_id.
 * Handles multiple UserAccess rows for a single project.
 */
export function resolveActiveProjectAccesses(userData, projectId) {
  if (!userData || !Array.isArray(userData.accesses) || !projectId) return [];
  const targetId = String(projectId).trim();
  return userData.accesses.filter(
    (access) =>
      String(access?.project_id).trim() === targetId &&
      (access?.active === undefined || access?.active === true)
  );
}

/**
 * Resolves and unions all unique roles assigned to the user for a specific project_id.
 */
export function resolveProjectRoles(userData, projectId) {
  const activeAccesses = resolveActiveProjectAccesses(userData, projectId);
  const rolesSet = new Set();

  activeAccesses.forEach((access) => {
    const rolesList = Array.isArray(access?.roles) ? access.roles : [];
    rolesList.forEach((r) => {
      const rawRole = typeof r === "string" ? r : r?.role || "";
      if (rawRole) {
        const normalized = String(rawRole)
          .trim()
          .toUpperCase()
          .replace(/[-\s]+/g, "_");
        rolesSet.add(normalized);
      }
    });
  });

  return Array.from(rolesSet);
}

/**
 * Resolves selected workflow role (FLOW_ROLE or ROLE) validated against the user's
 * project roles for active project.
 */
export function resolveSelectedWorkflowRole(userData, projectId) {
  const rawRole =
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ROLE") ||
    "";

  if (!rawRole) return null;

  const normalizedSelected = String(rawRole)
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

  const projectRoles = resolveProjectRoles(userData, projectId);

  if (projectRoles.length === 0) {
    return normalizedSelected;
  }

  if (projectRoles.includes(normalizedSelected)) {
    return normalizedSelected;
  }

  return null;
}

/**
 * Deduplicates a list of role names by canonical role representation.
 * Prevents duplicate entries such as "PROJECT_HEAD" and "Project Head" appearing together.
 */
export function deduplicateRoles(rolesList) {
  const roleMap = new Map();

  (rolesList || []).forEach((r) => {
    if (!r || typeof r !== "string") return;
    const trimmed = r.trim();
    if (!trimmed) return;
    const norm = trimmed.toLowerCase().replace(/[-_]+/g, " ");

    let canonical = trimmed;
    if (norm === "project head" || norm === "project manager") {
      canonical = "Project Head";
    }

    if (!roleMap.has(norm)) {
      roleMap.set(norm, canonical);
    } else {
      const existing = roleMap.get(norm);
      if (canonical === "Project Head") {
        roleMap.set(norm, canonical);
      } else if (existing === existing.toUpperCase() && canonical !== canonical.toUpperCase()) {
        roleMap.set(norm, canonical);
      }
    }
  });

  return Array.from(roleMap.values());
}